import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  CreatePaymentMethodDiscountRequest,
  DiscountType,
  FeeType,
  PaymentMethodDiscount,
  PaymentMethodType,
  UpdatePaymentMethodDiscountRequest,
} from "../../../types/admin";

interface PaymentMethodDiscountModalProps {
  open: boolean;
  mode: "create" | "edit";
  initialData?: PaymentMethodDiscount;
  onClose: () => void;
  onSave: (
    payload:
      | CreatePaymentMethodDiscountRequest
      | UpdatePaymentMethodDiscountRequest,
  ) => Promise<void> | void;
  submitting?: boolean;
}

const PaymentMethodDiscountModal: React.FC<
  PaymentMethodDiscountModalProps
> = ({ open, mode, initialData, onClose, onSave, submitting }) => {
  const { t } = useTranslation();
  const [paymentMethodType, setPaymentMethodType] = useState<PaymentMethodType>(
    initialData?.paymentMethodType ?? PaymentMethodType.Alif,
  );
  const [description, setDescription] = useState<string>(
    initialData?.description ?? "",
  );
  const [discountType, setDiscountType] = useState<DiscountType>(
    initialData?.discountType ?? DiscountType.Percentage,
  );
  const [discountValue, setDiscountValue] = useState<number>(
    initialData?.discountValue ?? 0,
  );
  const [feeType, setFeeType] = useState<FeeType>(
    initialData?.feeType ?? FeeType.ServiceFee,
  );
  const [isActive, setIsActive] = useState<boolean>(
    initialData?.isActive ?? true,
  );
  const [startDate, setStartDate] = useState<string>(
    initialData?.startDate
      ? new Date(initialData.startDate).toISOString().split("T")[0]
      : "",
  );
  const [endDate, setEndDate] = useState<string>(
    initialData?.endDate
      ? new Date(initialData.endDate).toISOString().split("T")[0]
      : "",
  );
  const [maxTotalUses, setMaxTotalUses] = useState<string>(
    initialData?.maxTotalUses?.toString() ?? "",
  );
  const [minOrderValue, setMinOrderValue] = useState<string>(
    initialData?.minOrderValue?.toString() ?? "",
  );
  const [notes, setNotes] = useState<string>(initialData?.notes ?? "");

  useEffect(() => {
    if (open) {
      setPaymentMethodType(
        initialData?.paymentMethodType ?? PaymentMethodType.Alif,
      );
      setDescription(initialData?.description ?? "");
      setDiscountType(
        initialData?.discountType ?? DiscountType.Percentage,
      );
      setDiscountValue(initialData?.discountValue ?? 0);
      setFeeType(initialData?.feeType ?? FeeType.ServiceFee);
      setIsActive(initialData?.isActive ?? true);
      setStartDate(
        initialData?.startDate
          ? new Date(initialData.startDate).toISOString().split("T")[0]
          : "",
      );
      setEndDate(
        initialData?.endDate
          ? new Date(initialData.endDate).toISOString().split("T")[0]
          : "",
      );
      setMaxTotalUses(initialData?.maxTotalUses?.toString() ?? "");
      setMinOrderValue(initialData?.minOrderValue?.toString() ?? "");
      setNotes(initialData?.notes ?? "");
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    const payload:
      | CreatePaymentMethodDiscountRequest
      | UpdatePaymentMethodDiscountRequest =
      mode === "create"
        ? {
            paymentMethodType,
            description,
            discountType,
            discountValue: Number(discountValue) || 0,
            feeType,
            isActive,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            maxTotalUses: maxTotalUses ? Number(maxTotalUses) : undefined,
            minOrderValue: minOrderValue ? Number(minOrderValue) : undefined,
            notes: notes || undefined,
          }
        : {
            paymentMethodType,
            description,
            discountType,
            discountValue: Number(discountValue) || 0,
            feeType,
            isActive,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            maxTotalUses: maxTotalUses ? Number(maxTotalUses) : undefined,
            minOrderValue: minOrderValue ? Number(minOrderValue) : undefined,
            notes: notes || undefined,
          };

    await onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {mode === "create"
          ? t("admin.createPaymentMethodDiscount")
          : t("admin.editPaymentMethodDiscount")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.paymentMethod")}</InputLabel>
              <Select
                value={paymentMethodType}
                onChange={(e) =>
                  setPaymentMethodType(e.target.value as PaymentMethodType)
                }
                label={t("admin.paymentMethod")}
              >
                <MenuItem value={PaymentMethodType.Alif}>{t("admin.alifPay")}</MenuItem>
                <MenuItem value={PaymentMethodType.DcCard}>{t("admin.dcCard")}</MenuItem>
                <MenuItem value={PaymentMethodType.Stripe}>
                  {t("admin.creditCardStripe")}
                </MenuItem>
                <MenuItem value={PaymentMethodType.PayPal}>{t("admin.paypal")}</MenuItem>
                <MenuItem value={PaymentMethodType.BankTransfer}>
                  {t("admin.bankTransfer")}
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t("admin.description")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              required
              helperText={t("admin.descriptionOfDiscountHelperText")}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t("admin.discountType")}</InputLabel>
                <Select
                  value={discountType}
                  onChange={(e) =>
                    setDiscountType(e.target.value as DiscountType)
                  }
                  label={t("admin.discountType")}
                >
                  <MenuItem value={DiscountType.Percentage}>{t("admin.percentage")}</MenuItem>
                  <MenuItem value={DiscountType.FixedAmount}>
                    {t("admin.fixedAmount")}
                  </MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={`${t("admin.discountValue")} ${
                  discountType === DiscountType.Percentage ? "(%)" : "($)"
                }`}
                type="number"
                value={discountValue}
                onChange={(e) =>
                  setDiscountValue(Number(e.target.value) || 0)
                }
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                required
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>{t("admin.feeType")}</InputLabel>
              <Select
                value={feeType}
                onChange={(e) => setFeeType(e.target.value as FeeType)}
                label={t("admin.feeType")}
              >
                <MenuItem value={FeeType.ServiceFee}>{t("admin.serviceFee")}</MenuItem>
                <MenuItem value={FeeType.CargoFee}>{t("admin.cargoFee")}</MenuItem>
                <MenuItem value={FeeType.AllFees}>{t("admin.allFees")}</MenuItem>
                <MenuItem value={FeeType.Tax}>{t("admin.tax")}</MenuItem>
                <MenuItem value={FeeType.TaxAndFees}>{t("admin.taxAndFees")}</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t("admin.startDate")}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={t("admin.endDate")}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t("admin.maxTotalUses")}
                type="number"
                value={maxTotalUses}
                onChange={(e) => setMaxTotalUses(e.target.value)}
                inputProps={{ min: 1 }}
                fullWidth
                helperText={t("admin.leaveEmptyForUnlimited")}
              />
              <TextField
                label={t("admin.minOrderValue")}
                type="number"
                value={minOrderValue}
                onChange={(e) => setMinOrderValue(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                helperText={t("admin.minOrderValueHelperText")}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label={t("common.active")}
            />
            <TextField
              label={t("admin.notes")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText={t("admin.optionalAdminNotes")}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !description}
        >
          {submitting
            ? t("common.saving")
            : mode === "create"
              ? t("common.create")
              : t("common.update")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentMethodDiscountModal;

